/**
 * Razorpay Checkout — Pro / Family / Bundle tiers.
 *
 * Live key from ~/.claude/vault/razorpay.md.
 * Checkout.js script injected on first openCheckout() call.
 */

declare global {
  interface Window {
    Razorpay?: new (opts: RazorpayOptions) => { open: () => void; on: (evt: string, cb: (data: unknown) => void) => void };
  }
}

const SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';
let loadPromise: Promise<void> | null = null;

export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: 'INR';
  name: string;
  description: string;
  image?: string;
  order_id?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  notes?: Record<string, string>;
  theme?: { color?: string };
  handler?: (response: { razorpay_payment_id: string; razorpay_order_id?: string; razorpay_signature?: string }) => void;
  modal?: { ondismiss?: () => void };
}

function loadScript(): Promise<void> {
  if (loadPromise !== null) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`);
    if (existing !== null) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = (): void => resolve();
    s.onerror = (): void => reject(new Error('razorpay script load failed'));
    document.head.appendChild(s);
  });
  return loadPromise;
}

export const RZP_KEY = 'rzp_live_SVzHpgz5EgB4bk';

export type Tier = 'pro_yearly' | 'family_yearly' | 'bundle_yearly';

export const PRICING: Record<Tier, { paise: number; label: string; description: string }> = {
  pro_yearly: { paise: 99_900, label: 'Pro · Yearly', description: 'Unlock genius features. 1 device' },
  family_yearly: { paise: 299_900, label: 'Family · Yearly', description: 'Up to 4 members' },
  bundle_yearly: { paise: 299_900, label: 'Empire Bundle', description: 'All 8 apps · 1 year' },
};

export async function openCheckout(
  tier: Tier,
  appName: string,
  prefill: { email?: string; contact?: string; name?: string } = {},
  onSuccess?: (paymentId: string) => void,
): Promise<void> {
  await loadScript();
  const Ctor = window.Razorpay;
  if (Ctor === undefined) throw new Error('razorpay unavailable');
  const tierCfg = PRICING[tier];
  const rz = new Ctor({
    key: RZP_KEY,
    amount: tierCfg.paise,
    currency: 'INR',
    name: appName,
    description: tierCfg.description,
    prefill,
    theme: { color: '#9b1c1c' },
    notes: { brand: appName, tier },
    handler: (response): void => {
      if (typeof onSuccess === 'function') onSuccess(response.razorpay_payment_id);
    },
  });
  rz.open();
}
